import { useTrading } from '@/hooks/useTrading';
import { DataTable } from '@/components/common/DataTable';
import { StatusBadge } from '@/components/common/StatusBadge';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { formatCurrency, formatDate } from '@/lib/utils';
import type { Order } from '@/types/trading';

const statusColors: Record<string, 'success' | 'warning' | 'error' | 'info' | 'neutral'> = {
  pending: 'info',
  submitted: 'info',
  filled: 'success',
  partially_filled: 'warning',
  cancelled: 'neutral',
  rejected: 'error',
  expired: 'neutral',
};

function computeOrderValue(order: Order): string {
  if (order.notional) return formatCurrency(order.notional);
  if (order.quantity && order.limit_price) return formatCurrency(order.quantity * order.limit_price);
  if (order.filled_quantity && order.filled_avg_price) {
    return formatCurrency(order.filled_quantity * order.filled_avg_price);
  }
  return '\u2014';
}

interface OrdersTableProps {
  orders?: Order[];
  isLoading?: boolean;
  showCancelButton?: boolean;
}

export function OrdersTable({
  orders: propOrders,
  isLoading: propLoading,
  showCancelButton = true,
}: OrdersTableProps) {
  const { orders: hookOrders, isLoadingOrders, cancelOrder, isCancelling } = useTrading();

  const orders = propOrders ?? hookOrders;
  const isLoading = propLoading ?? isLoadingOrders;

  const columns = [
    { header: 'Symbol', accessor: 'symbol' },
    {
      header: 'Side',
      accessor: 'side',
      render: (_: unknown, order: Order) => (
        <span className={order.side === 'buy' ? 'text-green-500' : 'text-red-500'}>
          {order.side.toUpperCase()}
        </span>
      ),
    },
    { header: 'Type', accessor: 'type' },
    {
      header: 'Qty',
      accessor: 'quantity',
      render: (_: unknown, order: Order) => (order.quantity != null ? String(order.quantity) : '\u2014'),
    },
    {
      header: 'Value',
      accessor: 'notional',
      render: (_: unknown, order: Order) => computeOrderValue(order),
    },
    {
      header: 'Price',
      accessor: 'limit_price',
      render: (_: unknown, order: Order) =>
        order.limit_price ? formatCurrency(order.limit_price) : 'Market',
    },
    { header: 'Filled', accessor: 'filled_quantity' },
    {
      header: 'Status',
      accessor: 'status',
      render: (_: unknown, order: Order) => (
        <StatusBadge status={statusColors[order.status] || 'neutral'}>
          {order.status.replace('_', ' ')}
        </StatusBadge>
      ),
    },
    {
      header: 'Time',
      accessor: 'created_at',
      render: (_: unknown, order: Order) => formatDate(order.created_at),
    },
    ...(showCancelButton
      ? [
          {
            header: '',
            accessor: 'actions',
            render: (_: unknown, order: Order) =>
              (order.status === 'pending' || order.status === 'submitted') && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => cancelOrder(order.id)}
                  disabled={isCancelling}
                >
                  <X className="h-4 w-4" />
                </Button>
              ),
          },
        ]
      : []),
  ];

  return (
    <DataTable
      columns={columns}
      data={orders}
      loading={isLoading}
      emptyMessage="No orders yet"
      rowKey={(order) => order.id}
    />
  );
}

export default OrdersTable;
